import { supabase } from '@/lib/supabase';
import { normalizePhone } from '@/lib/phoneUtils';
import type {
  CustomerPhone,
  PhoneInput,
  CustomerPhoneLookupResult,
} from '../types';

/**
 * Fetch all phones for a customer using RPC function
 */
export async function fetchCustomerPhones(
  customerId: string
): Promise<CustomerPhone[]> {
  const { data, error } = await supabase.rpc('get_customer_phones', {
    p_customer_id: customerId,
  });

  if (error) {
    console.error('[customerPhonesApi] Error fetching phones:', error);
    throw new Error(error.message);
  }

  return (data || []).map((phone: Record<string, unknown>) => ({
    ...phone,
    // RPC returns subset of fields, map to full interface
    business_id: '',
    customer_id: customerId,
    created_at: '',
    updated_at: '',
  })) as CustomerPhone[];
}

/**
 * Add a phone to a customer using RPC function
 * Phone number is automatically normalized by the database
 */
export async function addPhoneToCustomer(
  customerId: string,
  input: PhoneInput
): Promise<string> {
  const { data, error } = await supabase.rpc('add_phone_to_customer', {
    p_customer_id: customerId,
    p_phone_number: input.phone_number,
    p_phone_type: input.phone_type || 'mobile',
    p_is_primary: input.is_primary || false,
    p_can_sms: input.can_sms ?? true,
    p_label: input.label || null,
    p_notes: input.notes || null,
  });

  if (error) {
    console.error('[customerPhonesApi] Error adding phone:', error);
    throw new Error(error.message);
  }

  return data as string;
}

/**
 * Update an existing phone
 */
export async function updatePhone(
  phoneId: string,
  input: Partial<PhoneInput>
): Promise<void> {
  const updateData: Record<string, unknown> = {};

  if (input.phone_number !== undefined) {
    updateData.phone_number = normalizePhone(input.phone_number);
  }
  if (input.phone_type !== undefined) {
    updateData.phone_type = input.phone_type;
  }
  if (input.label !== undefined) {
    updateData.label = input.label || null;
  }
  if (input.can_sms !== undefined) {
    updateData.can_sms = input.can_sms;
  }
  if (input.notes !== undefined) {
    updateData.notes = input.notes || null;
  }

  const { error } = await supabase
    .from('customer_phones')
    .update(updateData)
    .eq('id', phoneId);

  if (error) {
    console.error('[customerPhonesApi] Error updating phone:', error);
    throw new Error(error.message);
  }
}

/**
 * Delete a phone
 */
export async function deletePhone(phoneId: string): Promise<void> {
  const { error } = await supabase
    .from('customer_phones')
    .delete()
    .eq('id', phoneId);

  if (error) {
    console.error('[customerPhonesApi] Error deleting phone:', error);
    throw new Error(error.message);
  }
}

/**
 * Set a phone as primary using RPC function
 */
export async function setPrimaryPhone(phoneId: string): Promise<void> {
  const { error } = await supabase.rpc('set_primary_phone', {
    p_phone_id: phoneId,
  });

  if (error) {
    console.error('[customerPhonesApi] Error setting primary phone:', error);
    throw new Error(error.message);
  }
}

/**
 * Find customers by phone number (caller ID lookup)
 * Input is automatically normalized
 */
export async function findCustomerByPhone(
  phoneNumber: string
): Promise<CustomerPhoneLookupResult[]> {
  const { data, error } = await supabase.rpc('find_customer_by_phone', {
    p_phone_number: phoneNumber,
  });

  if (error) {
    console.error('[customerPhonesApi] Error finding customer:', error);
    throw new Error(error.message);
  }

  return (data || []) as CustomerPhoneLookupResult[];
}

/**
 * Batch add phones to a customer (for customer creation flow)
 * Returns array of created phone IDs
 */
export async function addPhonesToCustomer(
  customerId: string,
  phones: PhoneInput[]
): Promise<string[]> {
  const results: string[] = [];

  for (const phone of phones) {
    // Skip empty phone numbers
    if (!phone.phone_number || !normalizePhone(phone.phone_number)) {
      continue;
    }

    try {
      const phoneId = await addPhoneToCustomer(customerId, phone);
      results.push(phoneId);
    } catch (error) {
      console.error('[customerPhonesApi] Error adding phone in batch:', error);
      // Continue with other phones even if one fails
    }
  }

  return results;
}
